import { FC, useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { format } from 'date-fns';
import { isNil } from 'lodash';
import { DailyNavRecordUpdate } from 'api/models/DailyNavRecordUpdate';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { FormDialog } from 'components';
import { REVIEW_PRICE_LABEL } from 'pages/Intelligence/DailyNav/dailyNav.constants';
import { useUpdateDailyNavRecord } from 'services/hooks/firms';
import { getObjectValue, getStringValue } from 'utilities';
import { ReviewPriceDialogProps, ReviewPriceFormValues } from './ReviewPriceDialog.props';
import { reviewPriceDialogColors as colors } from './ReviewPriceDialog.styles';
import ReviewPriceDialogContent from './ReviewPriceDialogContent';

const getFormValuesFromRow = (row: ReviewPriceDialogProps['row']): ReviewPriceFormValues => ({
  reviewed_by_scalar: row?.record.reviewed_by_scalar ?? false,
  discussed_with_fund: row?.record.discussed_with_fund ?? false,
  price_adjusted: row?.record.price_adjusted ?? false,
  review_notes: row?.record.review_notes ?? '',
});

// Notes are stored newline-delimited in review_notes, so a multiline draft must be
// flattened or it would split into separate thread entries on the next read.
const stampNote = (note: string, author: string) =>
  `${note.trim().replace(/\s*\n+\s*/g, ' ')} — ${author}, ${format(new Date(), 'MMM d, yyyy, h:mm a')}`;

const ReviewPriceDialog: FC<ReviewPriceDialogProps> = ({ row, open, onClose }) => {
  const [storeValue] = useStore() as UseStoreValues;
  const { user } = getObjectValue(storeValue);
  const author = getStringValue(user?.username ?? user?.email);

  const [formValues, setFormValues] = useState<ReviewPriceFormValues>(() => getFormValuesFromRow(row));
  const [noteDraft, setNoteDraft] = useState('');
  const { mutateAsync: updateDailyNavRecord, isPending } = useUpdateDailyNavRecord();

  useEffect(() => {
    setFormValues(getFormValuesFromRow(row));
    setNoteDraft('');
  }, [row]);

  const triggers = useMemo<string[]>(() => {
    const percentChange = row?.lookingGlass?.percent_change_from_previous;
    if (isNil(percentChange) || percentChange === 0) {
      return [];
    }
    const formatted = `${percentChange > 0 ? '+' : ''}${(percentChange * 100).toFixed(1)}%`;
    return [`Last Val Δ ${formatted} from previous measurement date`];
  }, [row]);

  const appendNoteDraft = () => {
    if (!noteDraft.trim()) {
      return;
    }
    const notes = formValues.review_notes.split('\n').filter(line => line.trim());
    setFormValues({ ...formValues, review_notes: [...notes, stampNote(noteDraft, author)].join('\n') });
    setNoteDraft('');
  };

  const handleSave = async () => {
    if (!row) {
      return;
    }
    // Fold any unsubmitted draft into the notes so typed text isn't lost on save.
    const notes = formValues.review_notes.split('\n').filter(line => line.trim());
    const reviewNotes = noteDraft.trim() ? [...notes, stampNote(noteDraft, author)].join('\n') : formValues.review_notes;

    // Status follows the review flags rather than being set on every save: a record
    // only counts as reviewed once a human checks "Reviewed by Scalar" or "Discussed
    // with Fund", matching dailyNavRecordNeedsReview. Saving notes or a price-adjusted
    // toggle without either flag leaves it in_progress.
    const isReviewed = formValues.reviewed_by_scalar || formValues.discussed_with_fund;
    const status = isReviewed
      ? DailyNavRecordUpdate.status.REVIEWED
      : DailyNavRecordUpdate.status.IN_PROGRESS;
    try {
      await updateDailyNavRecord({
        id: row.record.id as number,
        data: { ...formValues, review_notes: reviewNotes, status },
      });
      setNoteDraft('');
      onClose();
    } catch {
      // Keep the dialog open; the mutation's onError already surfaced a snackbar.
    }
  };

  return (
    <FormDialog
      title={REVIEW_PRICE_LABEL}
      open={open}
      editMode
      isValid
      isLoading={isPending}
      showSaveButton={false}
      onClose={onClose}
      customCancelButton={
        <Button
          id="review-price-cancel-button"
          onClick={onClose}
          disabled={isPending}
          sx={{
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            color: colors.slate700,
            fontSize: 12,
            paddingX: 1.5,
            paddingY: 0.5,
            textTransform: 'none',
          }}>
          Cancel
        </Button>
      }
      customButton={
        <Button
          id="review-price-save-button"
          onClick={handleSave}
          disabled={isPending}
          sx={{
            backgroundColor: colors.navy,
            borderRadius: '4px',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            paddingX: 1.5,
            paddingY: 0.5,
            textTransform: 'none',
            '&:hover': { backgroundColor: colors.navyHover },
          }}>
          Save review
        </Button>
      }>
      <ReviewPriceDialogContent
        row={row}
        triggers={triggers}
        formValues={formValues}
        setFormValues={setFormValues}
        noteDraft={noteDraft}
        setNoteDraft={setNoteDraft}
        onAddNote={appendNoteDraft}
      />
    </FormDialog>
  );
};

export default ReviewPriceDialog;
